import { useState } from 'react';
import './CharacterPanel.css';

export default function CharacterPanel({ character, onClose, onConsume }) {
  const [activeTab, setActiveTab] = useState('stats');

  const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1).replace(/-/g, ' ') : '';

  const getModifier = (score) => {
    const mod = Math.floor((score - 10) / 2);
    return mod >= 0 ? `+${mod}` : mod.toString();
  };

  const abilities = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];

  const tabs = [
    { id: 'stats', label: 'Stats' },
    { id: 'background', label: 'Background' },
    { id: 'inventory', label: 'Inventory' },
    { id: 'spells', label: 'Spells' }
  ];

  const getConsumableItems = () => {
    const consumableTypes = ['consumable', 'food', 'drink'];
    const foodKeywords = ['bread', 'food', 'ration', 'meat', 'fish', 'cheese', 'fruit', 'apple', 'berry', 'stew', 'soup', 'pie', 'cake', 'meal', 'jerky'];
    const drinkKeywords = ['water', 'waterskin', 'ale', 'wine', 'mead', 'juice', 'milk', 'tea', 'drink', 'flask', 'bottle', 'potion'];

    return character?.inventory?.filter(item => {
      if (consumableTypes.includes(item.type)) return true;
      const nameLower = item.name.toLowerCase();
      return foodKeywords.some(k => nameLower.includes(k)) || drinkKeywords.some(k => nameLower.includes(k));
    }) || [];
  };

  const renderStats = () => (
    <div className="stats-view">
      <div className="character-header">
        <h4>{character?.name}</h4>
        <p className="character-title">
          Level {character?.level} {capitalize(character?.species?.primary)} {capitalize(character?.class)}
        </p>
      </div>

      <div className="xp-bar">
        <span className="xp-label">XP: {character?.experience || 0}</span>
      </div>

      <div className="combat-stats">
        <div className="combat-stat">
          <span className="stat-value">{character?.armorClass || 10}</span>
          <span className="stat-name">AC</span>
        </div>
        <div className="combat-stat">
          <span className="stat-value">{getModifier(character?.abilityScores?.dexterity || 10)}</span>
          <span className="stat-name">Initiative</span>
        </div>
        <div className="combat-stat">
          <span className="stat-value">{character?.speed || 30}</span>
          <span className="stat-name">Speed</span>
        </div>
        <div className="combat-stat">
          <span className="stat-value">+{character?.proficiencyBonus || 2}</span>
          <span className="stat-name">Prof</span>
        </div>
      </div>

      <div className="ability-scores">
        {abilities.map(ability => (
          <div key={ability} className="ability-score">
            <span className="ability-name">{ability.slice(0, 3).toUpperCase()}</span>
            <span className="ability-value">{character?.abilityScores?.[ability] || 10}</span>
            <span className="ability-mod">{getModifier(character?.abilityScores?.[ability] || 10)}</span>
          </div>
        ))}
      </div>

      <div className="currency-display">
        <h5>Currency</h5>
        <div className="coins">
          <span className="coin copper">{character?.currency?.copper || 0} CP</span>
          <span className="coin silver">{character?.currency?.silver || 0} SP</span>
          <span className="coin gold">{character?.currency?.gold || 0} GP</span>
        </div>
      </div>

      <div className="languages-display">
        <h5>Languages</h5>
        <p>{character?.languages?.map(capitalize).join(', ') || 'Common'}</p>
      </div>
    </div>
  );

  const renderBackground = () => (
    <div className="background-view">
      <div className="background-section">
        <h5>Background</h5>
        <p className="background-name">{capitalize(character?.background?.name) || 'Unknown'}</p>
        {character?.background?.guild && (
          <p className="background-guild">Guild: {character.background.guild.name}</p>
        )}
      </div>

      <div className="appearance-section">
        <h5>Appearance</h5>
        <div className="appearance-stats">
          <span>Height: {character?.appearance?.height || 170}cm</span>
          <span>Weight: {character?.appearance?.weight || 70}kg</span>
          <span>Age: {character?.appearance?.age || 25} years</span>
        </div>
        {character?.appearance?.description ? (
          <p className="appearance-description">{character.appearance.description}</p>
        ) : (
          <p className="empty-message">No appearance description</p>
        )}
      </div>

      <div className="backstory-section">
        <h5>Backstory</h5>
        {character?.background?.story ? (
          <p className="backstory-text">{character.background.story}</p>
        ) : (
          <p className="empty-message">No backstory written</p>
        )}
      </div>
    </div>
  );

  const renderInventory = () => (
    <div className="inventory-view">
      <div className="consumables-section">
        <h5>Quick Use</h5>
        <div className="consumables-list">
          {getConsumableItems().length === 0 ? (
            <p className="empty-message">No consumable items</p>
          ) : (
            getConsumableItems().map((item, idx) => (
              <button
                key={idx}
                className="consumable-item"
                onClick={() => onConsume(item.name)}
                disabled={item.quantity <= 0}
              >
                <span className="item-name">{item.name}</span>
                <span className="item-qty">x{item.quantity}</span>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="full-inventory">
        <h5>All Items</h5>
        {character?.inventory?.length === 0 ? (
          <p className="empty-message">Your pack is empty</p>
        ) : (
          <div className="inventory-list">
            {character?.inventory?.map((item, idx) => (
              <div key={idx} className={`inventory-item ${item.equipped ? 'equipped' : ''}`}>
                <span className="item-name">
                  {item.name}
                  {item.equipped && <span className="equipped-badge">E</span>}
                </span>
                {item.quantity > 1 && <span className="item-qty">x{item.quantity}</span>}
                {item.properties?.damage && (
                  <span className="item-damage">{item.properties.damage}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderSpells = () => {
    const cantrips = character?.spells?.filter(s => s.level === 0) || [];
    const levelSpells = character?.spells?.filter(s => s.level > 0) || [];

    return (
      <div className="spells-view">
        {character?.spellSlots?.level1?.max > 0 && (
          <div className="spell-slots">
            <h5>Spell Slots</h5>
            <div className="slots-grid">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(level => {
                const slotKey = `level${level}`;
                const slots = character?.spellSlots?.[slotKey];
                if (!slots || slots.max === 0) return null;
                return (
                  <div key={level} className="slot-level">
                    <span className="slot-name">Lvl {level}</span>
                    <span className="slot-count">{slots.current}/{slots.max}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {cantrips.length > 0 && (
          <div className="spell-section">
            <h5>Cantrips</h5>
            {cantrips.map((spell, idx) => (
              <div key={idx} className="spell-item">
                <span className="spell-name">{spell.name}</span>
                <span className="spell-school">{spell.school}</span>
              </div>
            ))}
          </div>
        )}

        {levelSpells.length > 0 && (
          <div className="spell-section">
            <h5>Spells</h5>
            {levelSpells.map((spell, idx) => (
              <div key={idx} className={`spell-item ${spell.prepared ? 'prepared' : ''}`}>
                <span className="spell-name">
                  {spell.name}
                  {spell.prepared && <span className="prepared-badge">P</span>}
                </span>
                <span className="spell-level">Lvl {spell.level}</span>
              </div>
            ))}
          </div>
        )}

        {cantrips.length === 0 && levelSpells.length === 0 && (
          <p className="empty-message">No spells known</p>
        )}
      </div>
    );
  };

  return (
    <div className="side-panel character-panel">
      <div className="side-panel-header">
        <h3>Character</h3>
        <button className="close-btn" onClick={onClose}>&times;</button>
      </div>

      <div className="panel-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`panel-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="side-panel-content">
        {activeTab === 'stats' && renderStats()}
        {activeTab === 'background' && renderBackground()}
        {activeTab === 'inventory' && renderInventory()}
        {activeTab === 'spells' && renderSpells()}
      </div>
    </div>
  );
}
